import React, { useState, useEffect } from 'react';
import { useAlertsStore } from '../../stores/useAlertsStore';
import { BellIcon, XMarkIcon, CheckIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/useAuthStore';
import { logger } from '../../utils/logger';
import {
  fetchNotificacionesWeb,
  fetchNotificacionesCountWeb,
  marcarNotificacionLeidaWeb,
  marcarTodasNotificacionesLeidasWeb,
  deleteNotificacionWeb,
} from '../../lib/notificacionesApi';

interface Notificacion {
  id: string;
  tipo: 'STOCK_BAJO' | 'NUEVA_VENTA' | 'CLIENTE_CHURN' | 'BACKUP_COMPLETADO' | 'ALERTA_SISTEMA';
  titulo: string;
  mensaje: string;
  prioridad: 'BAJA' | 'MEDIA' | 'ALTA' | 'CRITICA';
  leida: boolean;
  fechaCreacion: string;
  datos?: Record<string, any>;
}

interface NotificacionesPushProps {
  className?: string;
}

const NotificacionesPush: React.FC<NotificacionesPushProps> = ({ className = '' }) => {
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [contadorNoLeidas, setContadorNoLeidas] = useState(0);
  const [cargando, setCargando] = useState(false);
  const { currentEmpresa, usuario } = useAuthStore();
  // Cuando el panel de Alertas cambia (quitar, marcar leído, preferencias), recontar ya.
  const alertasPendientes = useAlertsStore(s => s.unreadCount);
  const totalAlertas = useAlertsStore(s => s.alerts.length);
  const marcarLeidasLocal = useAlertsStore(s => s.marcarLeidasLocal);

  useEffect(() => {
    if (currentEmpresa?.id) cargarContador();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertasPendientes, totalAlertas]);

  useEffect(() => {
    if (currentEmpresa?.id) {
      cargarNotificaciones();
      cargarContador();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmpresa?.id, usuario?.id]);

  // Configurar intervalo para actualizar contador
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const intervalo = setInterval(() => {
      if (currentEmpresa?.id) {
        cargarContador();
      }
    }, 30000); // Actualizar cada 30 segundos

    return () => clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmpresa?.id, usuario?.id]);

  const mapRowToUi = (row: {
    id: string;
    tipo: string;
    mensaje: string;
    leida: boolean;
    created_at: string;
  }): Notificacion => ({
    id: row.id,
    tipo: 'ALERTA_SISTEMA',
    titulo: row.tipo.replace(/_/g, ' '),
    mensaje: row.mensaje,
    prioridad: 'MEDIA',
    leida: row.leida,
    fechaCreacion: row.created_at,
  });

  const cargarNotificaciones = async () => {
    try {
      setCargando(true);
      if (!currentEmpresa?.id) return;
      const rows = await fetchNotificacionesWeb(
        currentEmpresa.id,
        usuario?.id ?? null,
        10,
        usuario?.rol === 'ADMIN' || usuario?.rol === 'OWNER'
      );
      setNotificaciones(rows.map(mapRowToUi));
    } catch (error) {
      logger.debug('Notifications disabled:', error);
    } finally {
      setCargando(false);
    }
  };

  const cargarContador = async () => {
    try {
      if (!currentEmpresa?.id) return;
      const count = await fetchNotificacionesCountWeb(
        currentEmpresa.id,
        usuario?.id ?? null,
        usuario?.rol === 'ADMIN' || usuario?.rol === 'OWNER'
      );
      setContadorNoLeidas(count);
    } catch (error) {
      logger.debug('Notification count unavailable:', error);
    }
  };

  const marcarComoLeida = async (id: string) => {
    try {
      await marcarNotificacionLeidaWeb(id);
      setNotificaciones(prev =>
        prev.map(notif => (notif.id === id ? { ...notif, leida: true } : notif))
      );
      setContadorNoLeidas(prev => Math.max(0, prev - 1));
    } catch (error) {
      logger.debug('Error al marcar como leída:', error);
    }
  };

  const marcarTodasComoLeidas = async () => {
    try {
      if (!currentEmpresa?.id || !usuario?.id) return;
      await marcarTodasNotificacionesLeidasWeb(
        currentEmpresa.id,
        usuario.id,
        usuario?.rol === 'ADMIN' || usuario?.rol === 'OWNER'
      );
      setNotificaciones(prev => prev.map(notif => ({ ...notif, leida: true })));
      setContadorNoLeidas(0);
      marcarLeidasLocal(currentEmpresa.id);
    } catch (error) {
      logger.debug('Error al marcar todas como leídas:', error);
    }
  };

  const eliminarNotificacion = async (id: string) => {
    try {
      await deleteNotificacionWeb(id);
      setNotificaciones(prev => prev.filter(notif => notif.id !== id));
      const notifEliminada = notificaciones.find(n => n.id === id);
      if (notifEliminada && !notifEliminada.leida) {
        setContadorNoLeidas(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      logger.debug('Error al eliminar notificación:', error);
    }
  };

  const obtenerIconoPorTipo = (tipo: string) => {
    switch (tipo) {
      case 'STOCK_BAJO':
        return <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />;
      case 'NUEVA_VENTA':
        return <CheckIcon className="w-5 h-5 text-green-500" />;
      case 'CLIENTE_CHURN':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'BACKUP_COMPLETADO':
        return <CheckIcon className="w-5 h-5 text-blue-500" />;
      case 'ALERTA_SISTEMA':
        return <InformationCircleIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <BellIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const _obtenerColorPorPrioridad = (prioridad: string) => {
    switch (prioridad) {
      case 'CRITICA':
        return 'border-l-red-500 bg-red-50';
      case 'ALTA':
        return 'border-l-orange-500 bg-orange-50';
      case 'MEDIA':
        return 'border-l-yellow-500 bg-yellow-50';
      case 'BAJA':
        return 'border-l-blue-500 bg-blue-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const formatearFecha = (fecha: string) => {
    const date = new Date(fecha);
    const ahora = new Date();
    const diffMs = ahora.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} h`;
    if (diffDays < 7) return `Hace ${diffDays} días`;
    
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const toggleDropdown = () => {
    setMostrarDropdown(!mostrarDropdown);
    if (!mostrarDropdown) {
      cargarNotificaciones();
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Botón de notificaciones */}
      <button
        onClick={toggleDropdown}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-label="Notificaciones"
      >
        <BellIcon className="w-6 h-6" />
        
        {/* Badge de notificaciones no leídas */}
        {contadorNoLeidas > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {contadorNoLeidas > 99 ? '99+' : contadorNoLeidas}
          </span>
        )}
      </button>

      {/* Dropdown de notificaciones */}
      {mostrarDropdown && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">
              Notificaciones
            </h3>
            <div className="flex items-center space-x-2">
              {contadorNoLeidas > 0 && (
                <button
                  onClick={marcarTodasComoLeidas}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Marcar todas leídas
                </button>
              )}
              <button
                onClick={toggleDropdown}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Lista de notificaciones */}
          <div className="max-h-80 overflow-y-auto">
            {cargando ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2">Cargando notificaciones...</p>
              </div>
            ) : notificaciones.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <BellIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-lg font-medium">No hay notificaciones</p>
                <p className="text-sm">Estás al día con todo</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notificaciones.map((notificacion) => (
                  <div
                    key={notificacion.id}
                    className={`p-4 hover:bg-gray-50 transition-colors duration-150 ${
                      !notificacion.leida ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Icono */}
                      <div className="flex-shrink-0 mt-0.5">
                        {obtenerIconoPorTipo(notificacion.tipo)}
                      </div>

                      {/* Contenido */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${
                              !notificacion.leida ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notificacion.titulo}
                            </p>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {notificacion.mensaje}
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              {formatearFecha(notificacion.fechaCreacion)}
                            </p>
                          </div>

                          {/* Indicador de prioridad */}
                          <div className={`ml-2 w-2 h-2 rounded-full ${
                            notificacion.prioridad === 'CRITICA' ? 'bg-red-500' :
                            notificacion.prioridad === 'ALTA' ? 'bg-orange-500' :
                            notificacion.prioridad === 'MEDIA' ? 'bg-yellow-500' :
                            'bg-blue-500'
                          }`} />
                        </div>

                        {/* Acciones */}
                        <div className="flex items-center space-x-2 mt-3">
                          {!notificacion.leida && (
                            <button
                              onClick={() => marcarComoLeida(notificacion.id)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Marcar como leída
                            </button>
                          )}
                          <button
                            onClick={() => eliminarNotificacion(notificacion.id)}
                            className="text-xs text-red-600 hover:text-red-800 font-medium"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notificaciones.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50 text-center">
              <button
                onClick={cargarNotificaciones}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Ver todas las notificaciones
              </button>
            </div>
          )}
        </div>
      )}

      {/* Overlay para cerrar al hacer clic fuera */}
      {mostrarDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={toggleDropdown}
        />
      )}
    </div>
  );
};

export default NotificacionesPush;

